import axios from "axios";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid"; // Install: npm install uuid
import { logErrorWithPayload } from "./logService";
import { clearAuthToken, getAuthHeader } from "./authToken";
import config from "../config";

const { localurl, reportanalyticsurl } = config;

// Generate correlation ID for error tracking
const generateCorrelationId = () => {
  return `ERR-${Date.now()}-${uuidv4()
    .substring(0, 8)
    .toUpperCase()}`;
};

// Get user-friendly error details
const getUserFriendlyError = (status, error) => {
  const correlationId = generateCorrelationId();

  // Default fallback for unknown errors
  return {
    code: status?.toString() || "UNKNOWN",
    message: error.response?.data?.message || error.message,
    correlationId,
    shouldClearAuth: false,
  };
};

// Navigate to error page helper
const navigateToErrorPage = (errorInfo) => {
  const errorState = {
    code: errorInfo.code,
    title: errorInfo.title,
    message: errorInfo.message,
    correlationId: errorInfo.correlationId,
    timestamp: new Date().toISOString(),
  };

  // Store in sessionStorage for error page to read
  sessionStorage.setItem("lastError", JSON.stringify(errorState));

  // Dispatch custom event for App component to handle navigation
  window.dispatchEvent(
    new CustomEvent("navigateToError", {
      detail: errorState,
    }),
  );
};

// Safely parse a JSON string; returns the original value if it is not a JSON string.
const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
};

// Build a synthetic Error whose stack points to the application call site rather
// than Axios internals. The AxiosError thrown by `settle` contains no useful
// frames — by the time the response interceptor runs, the async context has
// unwound and the original caller (component, service, etc.) is gone from the
// stack. We work around this by capturing the stack eagerly in the request
// interceptor (still synchronous, so the full app call chain is intact) and
// replaying it here on the error object that goes to Sentry.
const buildCallerError = (callerStack, method, url, status, serverMessage) => {
  const label = `[${status}] ${(method || "HTTP").toUpperCase()} ${url}`;
  const message = serverMessage ? `${label}: ${serverMessage}` : label;

  const err = new Error(message);
  err.name = "ApiError";

  if (callerStack) {
    // Replace the first line (the Error() constructor text) with our label so
    // the Sentry title is readable, then keep all remaining frames as-is.
    const lines = callerStack.split("\n");
    lines[0] = `ApiError: ${label}`;
    err.stack = lines.join("\n");
  }

  return err;
};

const createInstance = (baseURL) => {
  const instance = axios.create({ baseURL });

  // Request interceptor
  instance.interceptors.request.use((config) => {
    const authHeader = getAuthHeader();

    if (authHeader) {
      config.headers.Authorization = authHeader;
    }

    // Capture the application call stack NOW, before the request goes async.
    // Axios request interceptors run synchronously so the full call chain —
    // including the component or service that initiated the request — is
    // still on the stack at this point. By the time the response error
    // interceptor fires, that context is completely gone.
    config.__sentryCallerStack = new Error().stack;

    // Add an HTTP breadcrumb so the Sentry event's Breadcrumbs trail shows
    // every API call that preceded the error, not just the failing one.
    Sentry.addBreadcrumb({
      type: "http",
      category: "fetch",
      message: `${(config.method || "GET").toUpperCase()} ${config.url}`,
      level: "info",
      data: {
        url: `${baseURL}${config.url}`,
        method: (config.method || "GET").toUpperCase(),
      },
    });

    return config;
  });

  // Response interceptor
  instance.interceptors.response.use(
    (res) => {
      let result = res && res.data ? res.data : res;
      if (
        res?.config?.responseType === "blob" ||
        res?.config?.responseType === "arraybuffer"
      ) {
        return Promise.resolve(result);
      }
      if (result?.success === false) {
        if (localStorage.getItem("titan_is_logging_out") === "true") {
          return Promise.reject(result);
        }
        // Even success:false responses should show error page
        const errorInfo = {
          code: res.status?.toString() || "ERROR",
          title: "Request Failed",
          message: result?.message || "The request could not be completed.",
          correlationId: generateCorrelationId(),
          shouldClearAuth: false,
        };

        logErrorWithPayload(new Error(result?.message || "Request failed"), {
          correlationId: errorInfo.correlationId,
          status: res.status,
          responseData: result,
          route: window.location.pathname,
          timestamp: new Date().toISOString(),
        });

        navigateToErrorPage(errorInfo);

        return Promise.reject(result);
      } else {
        result = result?.response || null;
      }
      return Promise.resolve(result);
    },
    (error) => {
      const status = error?.response?.status || -1;
      const requestConfig = error?.config || {};
      const requestHeaders = { ...(requestConfig.headers || {}) };

      // Redact authorization header for logging
      if (requestHeaders.Authorization) {
        requestHeaders.Authorization = "[REDACTED]";
      }

      // Axios serializes the request body to a JSON string; parse it back so Sentry
      // shows the actual object rather than a double-encoded escaped string.
      const parsedRequestData = tryParseJson(requestConfig.data);

      // Get user-friendly error info
      const errorInfo = getUserFriendlyError(status, error);

      // Build a synthetic error whose stack traces back to the component/service
      // that made the request, not into Axios `settle` internals.
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.title;
      const callerError = buildCallerError(
        requestConfig.__sentryCallerStack,
        requestConfig.method,
        requestConfig.url,
        status,
        serverMessage,
      );

      // Log with full API context so Sentry surfaces method, endpoint, status,
      // request body, and response body as structured data rather than a flat string.
      logErrorWithPayload(callerError, {
        correlationId: errorInfo.correlationId,
        status,
        method: requestConfig.method,
        url: requestConfig.url,
        baseURL: requestConfig.baseURL,
        params: requestConfig.params,
        data: parsedRequestData,
        headers: requestHeaders,
        responseData: error?.response?.data,
        route: window.location.pathname,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });

      // Mark the original error so component-level catch handlers know it has
      // already been sent to Sentry. Without this, a component that wraps the
      // call in try/catch and calls logError(err) creates a second, duplicate
      // event — and that second event is the one that shows the JSON blob title
      // because it goes through the generic logWithLevel path.
      error.__sentryLogged = true;

      // Clear auth token if needed (for 401 errors)
      if (errorInfo.shouldClearAuth) {
        clearAuthToken();
      }

      if (requestConfig.skipErrorPage !== true) {
        navigateToErrorPage(errorInfo);
      }

      // Attach correlation ID to error for component-level handling if needed
      error.correlationId = errorInfo.correlationId;
      error.userFriendlyMessage = errorInfo.message;

      return Promise.reject(error);
    },
  );

  return instance;
};

const localInstance = createInstance(localurl);
const reportAnalyticsInstance = createInstance(reportanalyticsurl);

const verbs = {
  httpGet: localInstance.get,
  httpPost: localInstance.post,
  httpPut: localInstance.put,
  httpDelete: localInstance.delete,
  httpGetReportAnalytics: reportAnalyticsInstance.get,
  httpPostReportAnalytics: reportAnalyticsInstance.post,
  httpPutReportAnalytics: reportAnalyticsInstance.put,
  httpDeleteReportAnalytics: reportAnalyticsInstance.delete,
};

export const {
  httpGet,
  httpPost,
  httpPut,
  httpDelete,
  httpGetReportAnalytics,
  httpPostReportAnalytics,
  httpPutReportAnalytics,
  httpDeleteReportAnalytics,
} = verbs || {};

export default verbs;
